<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" @click.self="$emit('close')">
    <div class="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
      <!-- Header -->
      <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 class="text-lg font-semibold text-gray-800">{{ template?.name }}</h2>
          <p class="text-sm text-gray-500">{{ form.contactName }}</p>
        </div>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
      </div>

      <!-- Body -->
      <div class="px-6 py-4 overflow-y-auto flex-1 space-y-4">
        <div v-for="field in visibleFields" :key="field.key">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ field.label }}
            <span v-if="field.required" class="text-red-500">*</span>
          </label>

          <!-- short_text -->
          <input
            v-if="field.type === 'short_text'"
            v-model="responses[field.key]"
            type="text"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />

          <!-- long_text -->
          <textarea
            v-else-if="field.type === 'long_text'"
            v-model="responses[field.key]"
            rows="3"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          ></textarea>

          <!-- select -->
          <select
            v-else-if="field.type === 'select'"
            v-model="responses[field.key]"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">-- Select --</option>
            <option v-for="opt in field.options" :key="opt" :value="opt">{{ opt }}</option>
          </select>

          <!-- multi_select -->
          <div v-else-if="field.type === 'multi_select'" class="space-y-1">
            <label v-for="opt in field.options" :key="opt" class="flex items-center space-x-2 text-sm">
              <input type="checkbox" :value="opt" v-model="responses[field.key]" class="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              <span>{{ opt }}</span>
            </label>
          </div>

          <!-- checkbox -->
          <label v-else-if="field.type === 'checkbox'" class="flex items-center space-x-2 text-sm">
            <input type="checkbox" v-model="responses[field.key]" class="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            <span>{{ field.label }}</span>
          </label>

          <!-- date -->
          <input
            v-else-if="field.type === 'date'"
            v-model="responses[field.key]"
            type="date"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />

          <!-- number -->
          <input
            v-else-if="field.type === 'number'"
            v-model.number="responses[field.key]"
            type="number"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div v-if="errorMsg" class="text-red-600 text-sm bg-red-50 p-3 rounded">{{ errorMsg }}</div>
      </div>

      <!-- Footer -->
      <div class="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
        <button @click="$emit('close')" class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
        <button
          @click="submit"
          :disabled="submitting"
          class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
        >
          <span v-if="submitting">Submitting...</span>
          <span v-else>Submit</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FormFillModal',
  props: {
    form: { type: Object, required: true },
    template: { type: Object, required: true },
    locationId: { type: String, required: true },
    companyId: { type: String, default: null },
    userName: { type: String, default: null }
  },
  emits: ['close', 'submitted'],
  data() {
    return {
      responses: {},
      submitting: false,
      errorMsg: null
    }
  },
  computed: {
    visibleFields() {
      return (this.template?.fields || []).filter(field => {
        if (!field.showWhen) return true
        const { field: depField, op, value } = field.showWhen
        const depValue = this.responses[depField]
        if (op === 'eq') return depValue === value
        if (op === 'neq') return depValue !== value
        return true
      })
    }
  },
  created() {
    const r = {}
    for (const field of this.template?.fields || []) {
      if (field.type === 'checkbox') r[field.key] = false
      else if (field.type === 'multi_select') r[field.key] = []
      else if (field.type === 'number') r[field.key] = null
      else r[field.key] = ''
    }
    this.responses = r
  },
  methods: {
    async submit() {
      this.errorMsg = null

      // Validate only visible required fields
      for (const field of this.visibleFields) {
        if (field.required) {
          const val = this.responses[field.key]
          if (val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0)) {
            this.errorMsg = `"${field.label}" is required`
            return
          }
        }
      }

      // Only include visible field responses
      const visibleKeys = new Set(this.visibleFields.map(f => f.key))
      const filteredResponses = {}
      for (const [key, value] of Object.entries(this.responses)) {
        if (visibleKeys.has(key)) {
          filteredResponses[key] = value
        }
      }

      this.submitting = true
      try {
        const body = {
          locationId: this.locationId,
          companyId: this.companyId,
          submittedBy: this.userName,
          responses: filteredResponses
        }
        const res = await fetch(`/opsflow/forms/${this.form.id}/submit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        })
        const data = await res.json()
        if (!res.ok) {
          this.errorMsg = data.error || 'Submission failed'
          return
        }
        this.$emit('submitted', data)
      } catch (e) {
        this.errorMsg = e.message || 'Network error'
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>
